import React, { useState, useEffect } from 'react';
import cacheManager from '../CacheManager';
import { formatDate, formatCurrency } from '../js/dateFormatter';

const PgMinhasFaturas = () => {
  const [faturas, setFaturas] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filtro, setFiltro] = useState('todas'); // todas, abertas, pagas, vencidas

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);
      const faturasCache = await cacheManager.getFaturasCache();
      setFaturas(faturasCache);
    } catch (error) {
      console.error('Erro ao carregar faturas:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getFaturasFiltradas = () => {
    let filtradas = [...faturas];
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);

    if (filtro === 'abertas') {
      filtradas = filtradas.filter(f => f.status === 'aberta' || f.status === 'pendente');
    } else if (filtro === 'pagas') {
      filtradas = filtradas.filter(f => f.status === 'paga');
    } else if (filtro === 'vencidas') {
      filtradas = filtradas.filter(f => {
        const dataVencimento = new Date(f.data_vencimento);
        dataVencimento.setHours(0, 0, 0, 0);
        return (f.status === 'aberta' || f.status === 'pendente') && dataVencimento < hoje;
      });
    }

    return filtradas.sort((a, b) => new Date(b.data_emissao) - new Date(a.data_emissao));
  };

  const getStatusBadge = (fatura) => {
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    const dataVencimento = new Date(fatura.data_vencimento);
    dataVencimento.setHours(0, 0, 0, 0);

    if (fatura.status === 'paga') {
      return <span className="badge bg-success">Paga</span>;
    } else if (fatura.status === 'cancelada') {
      return <span className="badge bg-secondary">Cancelada</span>;
    } else if (dataVencimento < hoje) {
      return <span className="badge bg-danger">Vencida</span>;
    } else {
      return <span className="badge bg-warning text-dark">Em Aberto</span>;
    }
  };

  const handleImprimirFatura = (idFatura) => {
    // TODO: Implementar impressão/download de fatura
    window.open(`https://api.faeworks.com.br/imprimir/fatura_externa.php?id=${idFatura}`, '_blank');
  };

  if (isLoading) {
    return (
      <div className="container mt-5 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Carregando...</span>
        </div>
      </div>
    );
  }

  const faturasFiltradas = getFaturasFiltradas();
  const totalEmAberto = faturas
    .filter(f => f.status === 'aberta' || f.status === 'pendente')
    .reduce((total, f) => total + parseFloat(f.valor_total || 0), 0);

  return (
    <div className="container mt-4">
      <div className="row mb-4">
        <div className="col-md-8">
          <h2>Minhas Faturas</h2>
          <p className="text-muted">Visualize e gerencie suas faturas</p>
        </div>
        <div className="col-md-4 text-end">
          <div className="card border-0 shadow-sm">
            <div className="card-body">
              <small className="text-muted">Total em Aberto</small>
              <h4 className="mb-0 text-danger">{formatCurrency(totalEmAberto)}</h4>
            </div>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="btn-group" role="group">
            <button
              type="button"
              className={`btn ${filtro === 'todas' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setFiltro('todas')}
            >
              Todas ({faturas.length})
            </button>
            <button
              type="button"
              className={`btn ${filtro === 'abertas' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setFiltro('abertas')}
            >
              Em Aberto
            </button>
            <button
              type="button"
              className={`btn ${filtro === 'vencidas' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setFiltro('vencidas')}
            >
              Vencidas
            </button>
            <button
              type="button"
              className={`btn ${filtro === 'pagas' ? 'btn-primary' : 'btn-outline-primary'}`}
              onClick={() => setFiltro('pagas')}
            >
              Pagas
            </button>
          </div>
        </div>
      </div>

      {/* Lista de Faturas */}
      <div className="row">
        <div className="col-12">
          {faturasFiltradas.length === 0 ? (
            <div className="card border-0 shadow-sm">
              <div className="card-body text-center py-5">
                <i className="bi bi-receipt fs-1 text-muted mb-3"></i>
                <p className="text-muted">Nenhuma fatura encontrada para este filtro.</p>
              </div>
            </div>
          ) : (
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead>
                      <tr>
                        <th>Nº Fatura</th>
                        <th>Data Emissão</th>
                        <th>Vencimento</th>
                        <th>Valor</th>
                        <th>Status</th>
                        <th>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {faturasFiltradas.map((fatura, index) => (
                        <tr key={index}>
                          <td className="fw-bold">#{fatura.id_fatura}</td>
                          <td>{formatDate(fatura.data_emissao)}</td>
                          <td>{formatDate(fatura.data_vencimento)}</td>
                          <td className="fw-bold">{formatCurrency(fatura.valor_total)}</td>
                          <td>{getStatusBadge(fatura)}</td>
                          <td>
                            <button
                              className="btn btn-sm btn-outline-primary"
                              onClick={() => handleImprimirFatura(fatura.id_fatura)}
                            >
                              <i className="bi bi-printer"></i> Imprimir
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Informação sobre Pagamento */}
      {faturasFiltradas.some(f => f.status === 'aberta' || f.status === 'pendente') && (
        <div className="row mt-4">
          <div className="col-12">
            <div className="alert alert-info">
              <h6 className="alert-heading">
                <i className="bi bi-info-circle"></i> Informações sobre Pagamento
              </h6>
              <p className="mb-0">
                Para realizar o pagamento de suas faturas, entre em contato conosco ou utilize os dados bancários 
                disponíveis na fatura impressa. Após o pagamento, informe-nos para que possamos confirmar.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PgMinhasFaturas;
